import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { DatePicker, Divider, Form, Radio, Select, Switch } from 'antd'
import type { RadioChangeEvent } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useLocalStorage } from '../hooks'
import type { ScheduleNewRateFormProps } from '../types'

export function ScheduleNewRateForm({ sortedHistory, onSubmit, closePopover }: ScheduleNewRateFormProps) {
  const [form] = Form.useForm()
  const [localCreateMode, setLocalCreateMode] = useState('copy')
  const [hasEndDate, setHasEndDate] = useLocalStorage('baseFreight_schedule_has_end_date', false)
  const [localEffectiveTo, setLocalEffectiveTo] = useState<dayjs.Dayjs | null>(null)

  // Calculate minimum allowed date using same logic as EffectiveDateForm
  const calculateMinimumDate = () => {
    if (sortedHistory.length === 0) return dayjs()

    const latestRate = sortedHistory[0] // Already sorted descending
    if (latestRate.effective_to) {
      return dayjs(latestRate.effective_to)
    }
    return dayjs(latestRate.effective_from).add(1, 'day')
  }

  const minimumAllowedDate = calculateMinimumDate()

  // Set initial values when the form is first rendered
  useEffect(() => {
    if (sortedHistory.length > 0) {
      form.setFieldsValue({
        create_mode: 'copy',
        copy_from_date_id: sortedHistory[0].id,
        effective_date: minimumAllowedDate,
      })
    }
  }, [form, sortedHistory, minimumAllowedDate])

  const handleModeChange = (e: RadioChangeEvent) => {
    const mode = e.target.value
    setLocalCreateMode(mode)
    form.setFieldsValue({ create_mode: mode })

    // Reset copy_from_date_id when switching to scratch mode
    if (mode === 'scratch') {
      form.setFieldsValue({ copy_from_date_id: undefined })
    } else if (sortedHistory.length > 0) {
      form.setFieldsValue({ copy_from_date_id: sortedHistory[0].id })
    }
  }

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setLocalEffectiveTo(date)
      form.setFieldsValue({ effective_to: date })
    }
  }

  return (
    <Form form={form} onFinish={onSubmit} layout='vertical'>
      <div style={{ padding: '8px 0' }}>
        <Texto category='p1' style={{ paddingBottom: 8 }}>
          Creation Mode
        </Texto>

        <Form.Item name='create_mode' noStyle>
          <Radio.Group onChange={handleModeChange} value={localCreateMode} style={{ width: '100%', marginBottom: 16 }}>
            <Radio.Button value='copy'>Copy from Existing</Radio.Button>
            <Radio.Button value='scratch'>Start from Scratch</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {localCreateMode === 'copy' && (
          <Form.Item
            name='copy_from_date_id'
            label='Copy from Date'
            style={{ marginBottom: 16 }}
            rules={[
              {
                required: true,
                message: 'Please select a date to copy from',
              },
            ]}
          >
            <Select
              options={sortedHistory.map((rate) => ({
                label: dayjs(rate.effective_from).format('MM/DD/YY'),
                value: rate.id,
              }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Divider />

        <Form.Item
          name='effective_date'
          label='Effective From'
          rules={[{ required: true, message: 'Please select an effective date' }]}
        >
          <DatePicker
            format='MM/DD/YYYY'
            disabledDate={(current) => current?.isBefore(minimumAllowedDate, 'day')}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Horizontal verticalCenter gap={8} style={{ marginBottom: 16 }}>
          <Switch checked={hasEndDate} onChange={setHasEndDate} />
          <Texto>{hasEndDate ? 'Set End Date' : 'No End Date'}</Texto>
        </Horizontal>

        {hasEndDate && (
          <Form.Item
            name='effective_to'
            label='Effective To'
            rules={[
              { required: true, message: 'Please select an end date' },
              {
                validator: (_, value) => {
                  if (value) {
                    const startDate = form.getFieldValue('effective_date')
                    if (startDate && value.isBefore(startDate)) {
                      return Promise.reject('End date must be after start date')
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <DatePicker
              format='MM/DD/YYYY'
              value={localEffectiveTo}
              onChange={handleEndDateChange}
              disabledDate={(current) => current && current.isBefore(form.getFieldValue('effective_date'))}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Horizontal justifyContent='flex-end' gap={8} style={{ marginTop: 16 }}>
          <GraviButton appearance='outlined' onClick={() => closePopover()}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 htmlType='submit'>
            Schedule
          </GraviButton>
        </Horizontal>
      </div>
    </Form>
  )
}
